import { useEffect } from "react";
import { RouteFactory } from "@app/routePaths";
import ButtonFactory from "@components/Button/ButtonFactory";
import { ButtonType } from "@components/Button/utils/types";
import Container from "@components/Container/Container";
import { GADataProps } from "@components/GAData/GAData";
import IconFactory from "@components/Icon/IconFactory";
import { IconTypeEnum } from "@components/Icon/utils/types";
import { Loading } from "@components/LoadMore/LoadMore";
import MyBTCarousel from "@features/mybt/components/Carousel/MyBTCarousel";
import useMyBtStore from "@features/mybt/store/useMyBTStore";
import { useUserInfo } from "@features/mySph/hooks/useUserInfo";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDelayedComponents } from "@hooks/useDelayedComponents";
import { useInjectClientData } from "@hooks/useInjectClientData";
import MyBTOnboardKeywordList from "@routes/MyBTOnboard/components/MyBTOnboardKeywordList";
import OnboardSearchBar from "@routes/MyBTOnboard/components/MyBTOnboardSearchBar";
import { useGetMyBTList } from "@routes/MyBTOnboard/hooks/useGetMyBTList";
import { MYBT_ONBOARD_SUBSCRIBE_FOR_MORE_URL } from "@routes/MyBTOnboard/utlis/constants";
import { MyBTOnboardPageData } from "@routes/MyBTOnboard/utlis/types";
import { MYBT_REG_LIMIT, MYBT_SUB_LIMIT } from "@util/constant";

/**
 * This is the content component for the MyBTOnboardPage.
 * It displays the search bar, the list of keywords, the regions and stocks carousel, and the subscribe for more button.
 */
const MyBTOnboardPageContent = ({
  myBTStocksData,
  myBTRegionsData,
  gaProps,
}: MyBTOnboardPageData & { gaProps: GADataProps }): React.ReactElement => {
  useInjectClientData({ gaProps });

  const { myBTList, isLoading } = useGetMyBTList();
  const [followedTagsStore] = useMyBtStore((store) => [store.tags]);

  const { isShown } = useDelayedComponents(900);
  const { visitorId, isSubscriber } = useUserInfo();

  useEffect(() => {
    if (!isShown) return;
    if (visitorId) return;
    if (typeof window === "undefined") return;

    window.location.href = RouteFactory.home;
  }, [isShown, visitorId]);

  const userTagLimit = isSubscriber ? MYBT_SUB_LIMIT : MYBT_REG_LIMIT;
  const progress_width = 100 * (followedTagsStore.length / userTagLimit);

  return (
    <>
      <div className="sticky left-0 right-0 top-0 z-10 w-full border-b border-gray-175 bg-white px-6 py-4">
        <div className="flex items-center justify-between gap-2 ">
          <OnboardSearchBar />

          <ButtonFactory
            buttonType={ButtonType.Link}
            className="transform rounded-full md:absolute md:right-6 md:top-6 "
            icon={<IconFactory type={IconTypeEnum.close} size={32} />}
            link={RouteFactory.myAccount}
            iconPosition="left"
          />
        </div>
      </div>

      <Container rootClassName="pb-3 pt-10 w-full px-3">
        <h2 className="w-full text-base font-bold uppercase text-gray-850 lg:text-center lg:text-3xl">
          Select your keywords
        </h2>
      </Container>

      <div className="w-full bg-gray-250 py-8">
        <Container>
          <MyBTOnboardKeywordList myBTList={myBTList} />
        </Container>
      </div>

      <Container rootClassName="pb-52">
        <MyBTCarousel
          myBTObjects={myBTRegionsData}
          title="regions"
          refCode={"mybt"}
        />

        <MyBTCarousel
          myBTObjects={myBTStocksData}
          title="stocks"
          refCode={"mybt"}
        />
      </Container>

      <div className="fixed bottom-0 left-0 right-0 z-10 border-t-2 border-gray-125 border-opacity-100 bg-white p-4">
        <div className="mx-auto w-full lg:w-3/10">
          <div className="mb-1 flex flex-wrap items-center justify-between">
            <p className="mb-0">
              {followedTagsStore.length} of {userTagLimit} keywords
            </p>

            {!isSubscriber ? (
              <ButtonFactory
                buttonType={ButtonType.Link}
                link={MYBT_ONBOARD_SUBSCRIBE_FOR_MORE_URL}
                text="SUBSCRIBE FOR MORE"
                className="items-center justify-center text-xs font-bold"
                icon={
                  <FontAwesomeIcon
                    icon={faChevronRight}
                    size="sm"
                    className="pl-1"
                  />
                }
                iconPosition="right"
              />
            ) : null}
          </div>

          <div className="mb-4 h-1 w-full bg-gray-200">
            <div
              className="h-1 bg-gray-850"
              style={{ width: `${progress_width}%` }}
            ></div>
          </div>

          <ButtonFactory
            buttonType={ButtonType.Link}
            mask={ButtonType.Primary}
            link={RouteFactory.myAccount}
            className="!text-white"
            text="SAVE AND SEE MY PAGE"
          />
        </div>
      </div>

      <Loading isLoading={isLoading} />
    </>
  );
};

export default MyBTOnboardPageContent;
